import {
  ComponentProps,
  FunctionComponent,
  ReactElement,
  ReactNode,
  RefObject,
  useEffect,
} from "react";
import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/solid";

import { CustomButton, IconButton } from "@helloaudio/ui";
import { noop } from "@helloaudio/utils";

interface ModalProps {
  actions?: Array<ReactElement<ComponentProps<typeof CustomButton>>>;
  children?: ReactNode;
  initialFocusRef?: RefObject<HTMLElement>;
  onClose?: () => void;
  title?: string;
  variant?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "6xl" | "full";
  visible?: boolean;
}
const Modal: FunctionComponent<ModalProps> = ({
  actions,
  children,
  initialFocusRef,
  onClose,
  title,
  variant = "md",
  visible = false,
}) => {
  useEffect(() => {
    if (visible) {
      const header = document.getElementById("ha_header");
      const main = document.getElementById("ha_main");
      const sidebar = document.getElementById("ha_sidebar");
      const topbar = document.getElementById("ha_topbar");
      [header, main, sidebar, topbar].forEach((elem) => elem?.classList?.add("tw-blur-xs"));
    }

    return () => {
      const header = document.getElementById("ha_header");
      const main = document.getElementById("ha_main");
      const sidebar = document.getElementById("ha_sidebar");
      const topbar = document.getElementById("ha_topbar");
      [header, main, sidebar, topbar].forEach((elem) => elem?.classList?.remove("tw-blur-xs"));
    };
  }, [visible]);

  return (
    <Dialog
      as="div"
      className="tw-relative tw-z-modal"
      initialFocus={initialFocusRef}
      open={visible}
      onClose={onClose || noop}
    >
      {/* Backdrop */}
      <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-25" />

      <div className="tw-fixed tw-inset-0 tw-overflow-y-auto">
        <div className="tw-flex tw-min-h-full tw-items-center tw-justify-center tw-p-4 tw-text-center">
          <Dialog.Panel
            className={`tw-w-full tw-max-w-${variant} tw-transform tw-rounded tw-bg-white tw-text-left tw-align-middle tw-transition-all`}
          >
            {(title || onClose) && (
              <div className="tw-h-auto tw-max-h-24 tw-px-4 tw-pr-2.5 tw-flex tw-items-center tw-border-solid tw-border-0 tw-border-b tw-border-gray-200">
                {title && (
                  <Dialog.Title className="title-semibold tw-grow tw-text-base tw-font-semibold tw-text-gray-900 tw-m-0 tw-py-4">
                    {title}
                  </Dialog.Title>
                )}

                {onClose && (
                  <IconButton
                    className="tw-ml-auto tw-border-none"
                    // circle
                    icon={XMarkIcon}
                    onClick={onClose}
                  />
                )}
              </div>
            )}

            <div>{children}</div>

            {actions && actions.length > 0 && (
              <div className="tw-h-14 tw-px-4 tw-pr-2.5 tw-flex tw-items-center tw-justify-end tw-gap-2 tw-bg-gray-100 tw-rounded-b">
                {actions}
              </div>
            )}
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
};

export default Modal;
